import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionExpiry } from '../hooks/useSessionExpiry';
import { toast } from '../lib/toast';
import styles from './SessionWarningDialog.module.css';

/**
 * Modal shown only when the session can no longer renew itself silently
 * (OIDC silent-renew failed or the user was signed out server-side). During
 * normal operation `react-oidc-context` renews the access token in the
 * background, so this never appears — see useSessionExpiry for why the old
 * 5-minute-countdown nag was removed.
 *
 * UX rules:
 * - Mounts at Layout level; renders nothing unless `isAboutToExpire`.
 * - Esc / backdrop click dismiss the current alert; it re-shows if the
 *   session hits another renewal failure.
 * - role="alertdialog" + focus trap + focus restore on close.
 */
const SessionWarningDialog: React.FC = () => {
  const { isAboutToExpire, refresh, signOut } = useSessionExpiry();

  // Local "dismissed" flag. Reset whenever `isAboutToExpire` transitions
  // from false -> true, so a later renewal failure re-shows the dialog even
  // if the student dismissed an earlier one.
  const [dismissed, setDismissed] = useState(false);
  const prevAboutToExpire = useRef(false);

  useEffect(() => {
    if (isAboutToExpire && !prevAboutToExpire.current) {
      // Re-entered the warning window — clear any prior dismissal.
      setDismissed(false);
    }
    prevAboutToExpire.current = isAboutToExpire;
  }, [isAboutToExpire]);

  const open = isAboutToExpire && !dismissed;

  // --- Focus management ----------------------------------------------------
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Remember whatever was focused so we can restore on close.
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Initial focus on the primary action — that's the recovery path.
    primaryBtnRef.current?.focus();

    return () => {
      // Restore focus when the dialog closes for any reason.
      const el = previouslyFocused.current;
      if (el && typeof el.focus === 'function') {
        try {
          el.focus();
        } catch {
          // Element may be gone (e.g. unmounted route); ignore.
        }
      }
    };
  }, [open]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Keyboard handling: Esc dismisses; Tab/Shift+Tab is trapped within the
  // two focusable buttons so screen-reader users can't tab into the page
  // behind the modal.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDismiss();
        return;
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusable = root.querySelectorAll<HTMLElement>(
          'button:not([disabled])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [handleDismiss]
  );

  // --- Refresh action ------------------------------------------------------
  const [refreshing, setRefreshing] = useState(false);

  const handleExtend = useCallback(async () => {
    setRefreshing(true);
    try {
      // refresh() retries silent renew; on failure it redirects to ZewstID
      // (navigates away), so a thrown error here is rare.
      await refresh();
      toast.success('Session restored', { id: 'session-restored' });
      setDismissed(true);
    } catch {
      toast.error('Could not restore session — please sign in again', {
        id: 'session-restore-failed',
      });
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        // Only treat clicks on the overlay itself as backdrop clicks —
        // ignore drags that start inside the modal and end on the overlay.
        if (e.target === e.currentTarget) {
          handleDismiss();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-warning-title"
        aria-describedby="session-warning-body"
        className={styles.modal}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <div className={styles.iconWrap} aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 7V12L15 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 id="session-warning-title" className={styles.title}>
            Your session has expired
          </h2>
        </div>
        <div id="session-warning-body" className={styles.body}>
          Sign in again to continue. Unsaved work in the editor may be lost.
        </div>
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={signOut}
            disabled={refreshing}
          >
            Sign out now
          </button>
          <button
            ref={primaryBtnRef}
            type="button"
            className={styles.primaryBtn}
            onClick={handleExtend}
            disabled={refreshing}
          >
            {refreshing && <span className={styles.spinner} aria-hidden="true" />}
            {refreshing ? 'Signing in…' : 'Sign in again'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionWarningDialog;
